import React, { useState, useMemo } from 'react';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import {
  getMealTypes,
  createMealType,
  updateMealType,
  deleteMealType,
} from '../services/api';
import useToast from '../hooks/useToast';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CurrencyRupeeIcon,
} from '@heroicons/react/24/outline';

const MealTypesManager = ({ eventId }) => {
  const { showSuccess, showError } = useToast();

  const {
    data: meals = [],
    isLoading,
    refetch,
  } = useApiQuery(
    ['meal-types', eventId],
    () => getMealTypes(eventId).then((r) => r.data),
    { enabled: Boolean(eventId) }
  );

  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // =====================================================
  // Mutations
  // =====================================================
  const createMutation = useApiMutation(
    (data) => createMealType(eventId, data),
    {
      onSuccess: () => {
        showSuccess('Meal added');
        setEditing(null);
        refetch();
      },
      onError: (e) =>
        showError(e.response?.data?.message || 'Failed'),
    }
  );

  const updateMutation = useApiMutation(
    ({ id, data }) => updateMealType(eventId, id, data),
    {
      onSuccess: () => {
        showSuccess('Meal updated');
        setEditing(null);
        refetch();
      },
      onError: (e) =>
        showError(e.response?.data?.message || 'Failed'),
    }
  );

  const deleteMutation = useApiMutation(
    (id) => deleteMealType(eventId, id),
    {
      onSuccess: () => {
        showSuccess('Meal deleted');
        refetch();
      },
      onError: (e) =>
        showError(e.response?.data?.message || 'Failed'),
    }
  );

  const handleSave = () => {
    if (!editing.name?.trim()) return showError('Name required');

    const payload = {
      name: editing.name.trim(),
      price: Number(editing.price) || 0,
      isActive: editing.isActive !== false,
    };

    if (editing._id) {
      updateMutation.mutate({ id: editing._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSaving =
    createMutation.isPending || updateMutation.isPending;

  // =====================================================
  // Stats
  // =====================================================
  const stats = useMemo(() => {
    const total = meals.length;
    const active = meals.filter((m) => m.isActive).length;
    const avgPrice =
      total > 0
        ? Math.round(
            meals.reduce((s, m) => s + (m.price || 0), 0) / total
          )
        : 0;
    return { total, active, avgPrice };
  }, [meals]);

  // =====================================================
  // Filter
  // =====================================================
  const filteredMeals = useMemo(() => {
    return meals.filter((m) => {
      const matchesSearch = m.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === 'all'
          ? true
          : filterStatus === 'active'
          ? m.isActive
          : !m.isActive;
      return matchesSearch && matchesStatus;
    });
  }, [meals, searchTerm, filterStatus]);

  // =====================================================
  // Loading
  // =====================================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-gray-300 border-t-[#1672b9] rounded-full animate-spin mb-3" />
          <p className="text-sm text-gray-500">Loading meals...</p>
        </div>
      </div>
    );
  }

  // =====================================================
  // UI
  // =====================================================
  return (
    <div className="space-y-6">
      {/* =================================================
          HEADER
      ================================================= */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Meal Types
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {stats.total} meal{stats.total !== 1 ? 's' : ''} configured
          </p>
        </div>

        <button
          onClick={() =>
            setEditing({ name: '', price: 0, isActive: true })
          }
          className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0f5a9e] transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Meal Type
        </button>
      </div>

      {/* =================================================
          STATS
      ================================================= */}
      <div className="grid grid-cols-3 divide-x divide-gray-200 bg-white border border-gray-200 rounded-lg">
        <div className="px-5 py-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Total
          </p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">
            {stats.total}
          </p>
        </div>
        <div className="px-5 py-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Active
          </p>
          <p className="text-2xl font-semibold text-[#1672b9] mt-1">
            {stats.active}
          </p>
        </div>
        <div className="px-5 py-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Avg. Price
          </p>
          <p className="text-2xl font-semibold text-[#1672b9] mt-1">
            ₹{stats.avgPrice}
          </p>
        </div>
      </div>

      {/* =================================================
          TOOLBAR
      ================================================= */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search meals..."
            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
          />
        </div>

        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'active', label: 'Active' },
            { id: 'inactive', label: 'Inactive' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilterStatus(f.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterStatus === f.id
                  ? 'bg-[#1672b9] text-white'
                  : 'text-gray-600 hover:bg-[#1672b9]/5 hover:text-[#1672b9]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* =================================================
          MEALS LIST
      ================================================= */}
      {meals.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg py-20 text-center">
          <p className="text-base font-medium text-gray-700">
            No meals configured
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Click "Add Meal Type" to create your first meal
          </p>
          <button
            onClick={() =>
              setEditing({ name: '', price: 0, isActive: true })
            }
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#1672b9] hover:underline"
          >
            <PlusIcon className="w-4 h-4" />
            Add Meal Type
          </button>
        </div>
      ) : filteredMeals.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg py-16 text-center">
          <p className="text-sm font-medium text-gray-700">
            No meals match your filters
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterStatus('all');
            }}
            className="mt-2 text-sm text-[#1672b9] hover:underline font-medium"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <div className="col-span-5">Meal</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100">
            {filteredMeals.map((m) => (
              <div
                key={m._id}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[#1672b9]/[0.02] transition-colors"
              >
                {/* Meal */}
                <div className="md:col-span-5">
                  <p className="text-sm font-medium text-gray-900">
                    {m.name}
                  </p>
                </div>

                {/* Price */}
                <div className="md:col-span-2">
                  <p className="text-sm font-semibold text-gray-900">
                    ₹{m.price || 0}
                  </p>
                </div>

                {/* Status */}
                <div className="md:col-span-2">
                  {m.isActive ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      Inactive
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="md:col-span-3 flex md:justify-end gap-2">
                  <button
                    onClick={() => setEditing({ ...m })}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:border-[#1672b9] hover:text-[#1672b9] transition-colors"
                  >
                    <PencilSquareIcon className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete "${m.name}"? This cannot be undone.`
                        )
                      ) {
                        deleteMutation.mutate(m._id);
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="inline-flex items-center justify-center px-2.5 py-1.5 text-gray-500 border border-gray-200 rounded-lg hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* =================================================
          MODAL
      ================================================= */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                {editing._id ? 'Edit Meal' : 'Add Meal'}
              </h3>
              <button
                onClick={() => setEditing(null)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Meal Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={editing.name}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  placeholder="e.g. Breakfast"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Price per Attendee
                </label>
                <div className="relative">
                  <CurrencyRupeeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    min="0"
                    value={editing.price}
                    onChange={(e) =>
                      setEditing({ ...editing, price: e.target.value })
                    }
                    className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Active
                  </p>
                  <p className="text-xs text-gray-500">
                    {editing.isActive !== false
                      ? 'Attendees can redeem this meal'
                      : 'Redemption is blocked'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setEditing({
                      ...editing,
                      isActive: editing.isActive === false,
                    })
                  }
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    editing.isActive !== false
                      ? 'bg-[#1672b9]'
                      : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                      editing.isActive !== false
                        ? 'translate-x-5'
                        : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setEditing(null)}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !editing.name?.trim()}
                className="bg-[#1672b9] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#0f5a9e] disabled:opacity-50 transition-colors"
              >
                {isSaving
                  ? 'Saving...'
                  : editing._id
                  ? 'Save'
                  : 'Add Meal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealTypesManager;